/**
 * formatters.ts — Centralized number formatting utilities
 *
 * All components must import from here rather than calling .toLocaleString()
 * individually. This ensures global consistency and makes it trivial to swap
 * locales or formatting conventions in one place.
 */

/**
 * Format an integer with comma-based thousands separators (en-US locale).
 *
 * Handles null, undefined, NaN, and negative values safely.
 *
 * @example
 * formatNumber(1250)      // "1,250"
 * formatNumber(1000000)   // "1,000,000"
 * formatNumber(0)         // "0"
 * formatNumber(null)      // "0"
 * formatNumber(undefined) // "0"
 * formatNumber(NaN)       // "0"
 */
export function formatNumber(num: number | null | undefined): string {
  if (num == null || !isFinite(num)) return '0';
  return Math.round(num).toLocaleString('en-US');
}

/**
 * Format a score value — alias for formatNumber, semantically clearer at call sites.
 */
export const formatScore = formatNumber;

/**
 * Format a coin amount — alias for formatNumber.
 */
export const formatCoins = formatNumber;

/**
 * Format a rank number (no thousands separator needed for typical ranks,
 * but kept consistent for very large leaderboards).
 */
export const formatRank = formatNumber;
